import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Globe, Plus, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import {
  AdminCompanyEmailDomainReasonSchema,
  ApproveBillingEntityDomainClaimRequestSchema,
  type AdminCreateBillingEntityDomainClaimRequest,
  type ApproveBillingEntityDomainClaimRequest,
  type BillingEntity,
  type BillingEntityDomainClaim,
  type BillingEntityOwnedOrganization,
} from '@reflex/shared';
import {
  getListAdminBillingEntityDomainClaimsQueryKey,
  useListAdminBillingEntityDomainClaims,
} from '@reflex/ui/client/generated-internal/admin';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Switch } from '@reflex/ui/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import { Input } from '@reflex/ui/components/ui/input';
import { Textarea } from '@reflex/ui/components/ui/textarea';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { ConfigBadge } from '@/components/status/ConfigBadge';
import { FormField } from '@/components/forms/FormField';
import { AutoJoinDestinationSelect } from '@/components/billing-entities/domain-claims/AutoJoinDestinationSelect';
import { claimErrorCopy } from '@/components/billing-entities/domain-claims/claim-error-copy';
import {
  adminClaimDomainSchema,
  type AdminClaimDomainForm,
} from '@/components/billing-entities/domain-claims/claim-domain-schema';
import { toastVerifyOutcome } from '@/components/billing-entities/domain-claims/domain-claim-toasts';
import { ApiError, api } from '@/lib/api';
import { DOMAIN_CLAIM_STATUS_BADGE } from '@/lib/status-colors';
import { formatDateLong, formatRelativeTime } from '@reflex/ui/lib/format';

type PolicyPatch = { ssoDiscoveryEnabled?: boolean; autoJoinOrganizationId?: string | null };

const ReasonFormSchema = z.object({ reason: AdminCompanyEmailDomainReasonSchema });
type ReasonForm = z.infer<typeof ReasonFormSchema>;

/** One sentence naming the routing change the admin is about to make. */
function describePolicyChange(
  patch: PolicyPatch,
  organizations: readonly Pick<BillingEntityOwnedOrganization, 'id' | 'name'>[],
): string {
  if (patch.ssoDiscoveryEnabled !== undefined) {
    return patch.ssoDiscoveryEnabled ? 'Turns SSO discovery on.' : 'Turns SSO discovery off.';
  }
  if (patch.autoJoinOrganizationId === null) return 'Clears the auto-join destination.';
  const name =
    organizations.find((org) => org.id === patch.autoJoinOrganizationId)?.name ??
    'the selected organization';
  return `Sends auto-join sign-ups to ${name}.`;
}

/**
 * Whether support may mark this claim verified without DNS proof. A claim
 * DNS already verifies is left alone: a manual verification would take it
 * off the daily re-check, and the server refuses it. A plain
 * platform-approved claim has nothing left to vouch for; a grandfathered
 * one still lacks vetting.
 */
function canApproveWithoutDns(claim: BillingEntityDomainClaim): boolean {
  if (claim.method === 'platform-approved') return claim.grandfathered;
  return claim.status !== 'verified';
}

/**
 * Whether a DNS check means anything for this claim. Mirrors the server: an
 * approval support gave on purpose is not re-litigated through public DNS,
 * but a grandfathered claim can still be proved.
 */
function canCheckDns(claim: BillingEntityDomainClaim): boolean {
  return claim.method !== 'platform-approved' || claim.grandfathered;
}

/**
 * The customer copy addresses the company ("contact support", "your
 * company"); support reading it here is the one it points at.
 */
const ADMIN_CLAIM_ERROR_COPY = {
  domain_already_claimed: {
    title: 'Another company owns this domain',
    description: 'A domain belongs to one company. Reassign it from the Email domains admin tools.',
  },
  too_many_domain_claims: {
    title: 'Claim limit reached',
    description: 'This company holds the maximum number of domain claims. Remove one first.',
  },
  domain_claim_exists: {
    title: 'This company already claims that domain',
    description: 'Its claim is listed on this page.',
  },
};

/** Toast copy for a server error, preferring the typed domain-claim refusals. */
function toastClaimError(err: unknown, fallbackTitle: string): void {
  const copy = claimErrorCopy(err, ADMIN_CLAIM_ERROR_COPY);
  toast.error(copy?.title ?? fallbackTitle, {
    description: copy?.description ?? (err instanceof Error ? err.message : undefined),
  });
}

export interface AdminBillingEntityDomainClaimsSectionProps {
  /**
   * Structural, not `BillingEntity` from `@reflex/shared`: the page passes
   * the generated client's view of the row, whose `id` is a plain `string`
   * rather than the branded `ent_${string}`. This card only needs the id.
   */
  billingEntity: Pick<BillingEntity, 'name'> & { id: string };
}

/**
 * Card that lists the billing entity's email-domain claims and gives
 * platform admins the support levers: claiming a domain on the company's
 * behalf (it still lands pending and needs DNS proof), checking DNS now,
 * verifying a claim without public DNS proof (dedicated-VPC split-horizon
 * exception), overriding a claim's consumption policy (SSO discovery,
 * auto-join destination), and removing a claim. Every lever but the DNS
 * check takes a written reason that lands in the company's audit trail;
 * DNS, not the admin, decides a check. Claims belong to the billing entity —
 * the commercial owner of one or more organizations — so a standalone org
 * has nowhere to hang a claim until it is attached to one.
 */
export function AdminBillingEntityDomainClaimsSection({
  billingEntity,
}: AdminBillingEntityDomainClaimsSectionProps) {
  const queryClient = useQueryClient();
  const [approveTarget, setApproveTarget] = useState<BillingEntityDomainClaim | null>(null);
  const [policyTarget, setPolicyTarget] = useState<{
    claim: BillingEntityDomainClaim;
    patch: PolicyPatch;
  } | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<BillingEntityDomainClaim | null>(null);
  const billingEntityId = billingEntity.id;

  // Generated hook and generated query key, the same way the org-admin
  // surface reads its claims — no hand-maintained key to drift.
  const claimsQuery = useListAdminBillingEntityDomainClaims(billingEntityId);
  const claims = claimsQuery.data?.claims ?? [];
  const organizations = claimsQuery.data?.organizations ?? [];

  function invalidate(): void {
    void queryClient.invalidateQueries({
      queryKey: getListAdminBillingEntityDomainClaimsQueryKey(billingEntityId),
    });
  }

  const approveForm = useForm<ApproveBillingEntityDomainClaimRequest>({
    resolver: zodResolver(ApproveBillingEntityDomainClaimRequestSchema),
    defaultValues: { reason: '' },
  });

  function closeApproveDialog(): void {
    setApproveTarget(null);
    approveForm.reset();
  }

  const approveMutation = useMutation({
    mutationFn: ({ claimId, reason }: { claimId: string; reason: string }) =>
      api.approveAdminBillingEntityDomainClaim(billingEntityId, claimId, reason),
    onSuccess: (claim) => {
      toast.success(`${claim.domain} approved without DNS proof`);
      closeApproveDialog();
      invalidate();
    },
    onError: (err) => {
      // Approving is idempotent server-side, so there is no "already
      // approved" conflict to report; the realistic failure is the claim
      // having been removed since this list was loaded.
      const description =
        err instanceof ApiError && err.status === 404
          ? 'This claim no longer exists. Refresh the page.'
          : err instanceof Error
            ? err.message
            : undefined;
      toast.error('Could not approve the claim', { description });
    },
  });

  // Support is changing another company's routing, so the change waits on
  // a written reason for the audit trail, like approval does.
  const policyForm = useForm<ReasonForm>({
    resolver: zodResolver(ReasonFormSchema),
    defaultValues: { reason: '' },
  });

  function openPolicyDialog(claim: BillingEntityDomainClaim, patch: PolicyPatch): void {
    policyForm.reset();
    setPolicyTarget({ claim, patch });
  }

  function closePolicyDialog(): void {
    setPolicyTarget(null);
    policyForm.reset();
  }

  const policyMutation = useMutation({
    mutationFn: ({
      claimId,
      patch,
      reason,
    }: {
      claimId: string;
      patch: PolicyPatch;
      reason: string;
    }) => api.updateAdminBillingEntityDomainClaimPolicy(billingEntityId, claimId, patch, reason),
    onSuccess: () => {
      toast.success('Claim policy updated');
      closePolicyDialog();
      invalidate();
    },
    // Same typed refusals the customer surface explains, same copy.
    onError: (err) => toastClaimError(err, 'Could not update the claim policy'),
  });

  // The customer form's client-side domain check, so an unclaimable domain
  // is refused inline instead of by the server.
  const addForm = useForm<
    AdminClaimDomainForm,
    unknown,
    AdminCreateBillingEntityDomainClaimRequest
  >({
    resolver: zodResolver(adminClaimDomainSchema),
    defaultValues: { domain: '', reason: '' },
  });

  function closeAddDialog(): void {
    setAddOpen(false);
    addForm.reset();
  }

  const addMutation = useMutation({
    mutationFn: (input: AdminCreateBillingEntityDomainClaimRequest) =>
      api.createAdminBillingEntityDomainClaim(billingEntityId, input),
    onSuccess: (claim) => {
      toast.success(`Claimed ${claim.domain}`, {
        description:
          'Send the company the TXT record shown on its claim, then check DNS once it is published.',
      });
      closeAddDialog();
      invalidate();
    },
    onError: (err) => toastClaimError(err, 'Could not claim the domain'),
  });

  const verifyMutation = useMutation({
    mutationFn: (claimId: string) =>
      api.verifyAdminBillingEntityDomainClaim(billingEntityId, claimId),
    onSuccess: (result) => {
      invalidate();
      toastVerifyOutcome(result);
    },
    onError: (err) => toastClaimError(err, 'Could not check DNS'),
  });

  const removeForm = useForm<ReasonForm>({
    resolver: zodResolver(ReasonFormSchema),
    defaultValues: { reason: '' },
  });

  function closeRemoveDialog(): void {
    setRemoveTarget(null);
    removeForm.reset();
  }

  const removeMutation = useMutation({
    mutationFn: ({ claim, reason }: { claim: BillingEntityDomainClaim; reason: string }) =>
      api.removeAdminBillingEntityDomainClaim(billingEntityId, claim.id, reason),
    onSuccess: (_result, { claim }) => {
      toast.success(`Removed the claim on ${claim.domain}`);
      closeRemoveDialog();
      invalidate();
    },
    onError: (err) => toastClaimError(err, 'Could not remove the claim'),
  });

  return (
    <SectionCard
      title="Domain claims"
      description="Email domains this company claims. Each is proved with a DNS TXT record, and verified claims can route SSO discovery and auto-join sign-ups for the company's organizations."
      icon={<Globe />}
      contentClassName="pt-0"
      action={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            addForm.reset();
            setAddOpen(true);
          }}
          data-testid="admin-billing-entity-domain-claim-add"
        >
          <Plus className="icon-sm" /> Add domain
        </Button>
      }
    >
      <div className="space-y-3" data-testid="admin-billing-entity-domain-claims">
        <AsyncContent
          isLoading={claimsQuery.isLoading}
          isError={claimsQuery.isError}
          errorMessage="Failed to load domain claims."
        >
          {claims.length === 0 ? (
            <EmptyState
              variant="bordered"
              icon={<Globe className="h-8 w-8" />}
              title="No domain claims yet"
              description="Add a domain here, or a Company Manager can claim one on the Company page."
            />
          ) : (
            <div className="space-y-3">
              {claims.map((claim) => {
                return (
                  <div
                    key={claim.id}
                    className="space-y-3 rounded-lg border p-3"
                    data-testid={`admin-billing-entity-domain-claim-row-${claim.id}`}
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-medium">{claim.domain}</span>
                        <span data-testid={`admin-billing-entity-domain-claim-status-${claim.id}`}>
                          <ConfigBadge config={DOMAIN_CLAIM_STATUS_BADGE} value={claim.status} />
                        </span>
                        {claim.method === 'platform-approved' && (
                          <Badge
                            variant="secondary"
                            data-testid={`admin-billing-entity-domain-claim-method-${claim.id}`}
                          >
                            {/* Migrated, never vetted: not attach proof until approved. */}
                            {claim.grandfathered ? 'Grandfathered' : 'Platform approved'}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {canCheckDns(claim) && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            // Only the claim being checked waits; the others stay usable.
                            disabled={
                              verifyMutation.isPending && verifyMutation.variables === claim.id
                            }
                            onClick={() => verifyMutation.mutate(claim.id)}
                            aria-label={`Check DNS for ${claim.domain} now`}
                            data-testid={`admin-billing-entity-domain-claim-verify-${claim.id}`}
                          >
                            <RefreshCw className="icon-sm" /> Check DNS
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            removeForm.reset();
                            setRemoveTarget(claim);
                          }}
                          aria-label={`Remove claim on ${claim.domain}`}
                          data-testid={`admin-billing-entity-domain-claim-remove-${claim.id}`}
                        >
                          <Trash2 className="icon-sm" /> Remove
                        </Button>
                        {canApproveWithoutDns(claim) && (
                          // The override, not a routine step: it marks the
                          // claim verified with no DNS check, so it sits
                          // last and reads as what it is.
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              approveForm.reset();
                              setApproveTarget(claim);
                            }}
                            aria-label={`Verify ${claim.domain} without DNS proof`}
                            data-testid={`admin-billing-entity-domain-claim-approve-${claim.id}`}
                          >
                            <ShieldCheck className="icon-sm" /> Verify without DNS proof
                          </Button>
                        )}
                      </div>
                    </div>
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-xs text-muted-foreground sm:grid-cols-2">
                      <div>
                        <SectionLabel as="dt">DNS record</SectionLabel>
                        <dd className="mt-0.5 break-all font-mono">{claim.recordName}</dd>
                      </div>
                      <div>
                        <SectionLabel as="dt">Challenge token</SectionLabel>
                        {/* Public proof material published in DNS, never a secret. */}
                        <dd className="mt-0.5 break-all font-mono">{claim.challengeToken}</dd>
                      </div>
                      {claim.verifiedAt !== null && (
                        <div>
                          <SectionLabel as="dt">Verified</SectionLabel>
                          <dd className="mt-0.5">{formatDateLong(claim.verifiedAt)}</dd>
                        </div>
                      )}
                      {claim.lastCheckedAt !== null && (
                        <div>
                          <SectionLabel as="dt">Last checked</SectionLabel>
                          <dd className="mt-0.5">{formatRelativeTime(claim.lastCheckedAt)}</dd>
                        </div>
                      )}
                      {claim.failedChecks > 0 && (
                        <div>
                          <SectionLabel as="dt">Failed checks</SectionLabel>
                          <dd
                            className="mt-0.5 text-destructive"
                            data-testid={`admin-billing-entity-domain-claim-failed-checks-${claim.id}`}
                          >
                            {claim.failedChecks} consecutive
                          </dd>
                        </div>
                      )}
                    </dl>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={claim.ssoDiscoveryEnabled}
                          // Only enabling needs proof, as on the customer row.
                          disabled={
                            (claim.status !== 'verified' && !claim.ssoDiscoveryEnabled) ||
                            policyMutation.isPending
                          }
                          onCheckedChange={(next) =>
                            openPolicyDialog(claim, { ssoDiscoveryEnabled: next })
                          }
                          aria-label={`Toggle SSO discovery for ${claim.domain}`}
                          data-testid={`admin-billing-entity-domain-claim-sso-${claim.id}`}
                        />
                        <span className="text-sm">SSO discovery</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Auto-join</span>
                        <AutoJoinDestinationSelect
                          className="w-56"
                          value={claim.autoJoinOrganizationId}
                          organizations={organizations}
                          // Naming a destination needs proof; clearing one never does.
                          disabled={
                            (claim.status !== 'verified' &&
                              claim.autoJoinOrganizationId === null) ||
                            policyMutation.isPending
                          }
                          ariaLabel={`Auto-join destination for ${claim.domain}`}
                          data-testid={`admin-billing-entity-domain-claim-auto-join-${claim.id}`}
                          onChange={(autoJoinOrganizationId) =>
                            openPolicyDialog(claim, { autoJoinOrganizationId })
                          }
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </AsyncContent>
      </div>
      <Dialog
        open={approveTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeApproveDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Verify {approveTarget?.domain ?? 'this domain'} without DNS proof
            </DialogTitle>
            <DialogDescription>
              Marks the domain verified with no DNS check, now or later: the claim leaves the daily
              re-check for good. Reserved for dedicated VPC deployments whose split-horizon DNS
              cannot publish the challenge publicly. The reason is recorded in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={approveForm.handleSubmit((values) => {
              if (approveTarget) {
                approveMutation.mutate({ claimId: approveTarget.id, reason: values.reason });
              }
            })}
          >
            <FormField
              label="Reason"
              htmlFor="admin-domain-claim-approve-reason"
              required
              // The shared schema enforces the bounds; the raw Zod message is
              // not operator-facing copy, so state the rule ourselves.
              error={
                approveForm.formState.errors.reason
                  ? 'Give a reason between 10 and 2000 characters.'
                  : undefined
              }
              hint="Recorded with your user in the enterprise identity audit trail."
            >
              <Textarea
                id="admin-domain-claim-approve-reason"
                rows={4}
                placeholder="Why public DNS proof is impossible for this deployment"
                {...approveForm.register('reason')}
                data-testid="admin-domain-claim-approve-reason"
              />
            </FormField>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeApproveDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={approveMutation.isPending}
                data-testid="admin-domain-claim-approve-submit"
              >
                Verify without DNS proof
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={policyTarget !== null}
        onOpenChange={(open) => {
          if (!open) closePolicyDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Change routing for {policyTarget?.claim.domain ?? 'this domain'}
            </DialogTitle>
            <DialogDescription>
              {policyTarget ? describePolicyChange(policyTarget.patch, organizations) : null} This
              changes where the company's sign-ins go. The reason is recorded in the company's audit
              trail as a platform admin change.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={policyForm.handleSubmit((values) => {
              if (policyTarget) {
                policyMutation.mutate({
                  claimId: policyTarget.claim.id,
                  patch: policyTarget.patch,
                  reason: values.reason,
                });
              }
            })}
          >
            <FormField
              label="Reason"
              htmlFor="admin-domain-claim-policy-reason"
              required
              error={
                policyForm.formState.errors.reason
                  ? 'Give a reason of up to 500 characters.'
                  : undefined
              }
              hint="Recorded with your user in the enterprise identity audit trail."
            >
              <Textarea
                id="admin-domain-claim-policy-reason"
                rows={3}
                placeholder="Why support is changing this routing, and the ticket"
                {...policyForm.register('reason')}
                data-testid="admin-domain-claim-policy-reason"
              />
            </FormField>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closePolicyDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={policyMutation.isPending}
                data-testid="admin-domain-claim-policy-submit"
              >
                Change routing
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          if (!open) closeAddDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a domain to {billingEntity.name}</DialogTitle>
            <DialogDescription>
              Issues a DNS TXT challenge for the domain. The claim stays pending until the company
              publishes the record and DNS confirms it, or you verify it without DNS proof. The
              reason is recorded in the company's audit trail as a platform admin change.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={addForm.handleSubmit((values) => addMutation.mutate(values))}
          >
            <FormField
              label="Domain"
              htmlFor="admin-domain-claim-add-domain"
              required
              error={addForm.formState.errors.domain?.message}
              hint="The email domain only, like example.com."
            >
              <Input
                id="admin-domain-claim-add-domain"
                placeholder="example.com"
                autoComplete="off"
                {...addForm.register('domain')}
                data-testid="admin-domain-claim-add-domain"
              />
            </FormField>
            <FormField
              label="Reason"
              htmlFor="admin-domain-claim-add-reason"
              required
              error={
                addForm.formState.errors.reason
                  ? 'Give a reason of up to 500 characters.'
                  : undefined
              }
              hint="Recorded with your user in the enterprise identity audit trail."
            >
              <Textarea
                id="admin-domain-claim-add-reason"
                rows={3}
                placeholder="Why support is claiming this domain, and the ticket"
                {...addForm.register('reason')}
                data-testid="admin-domain-claim-add-reason"
              />
            </FormField>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeAddDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={addMutation.isPending}
                data-testid="admin-domain-claim-add-submit"
              >
                Add domain
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeRemoveDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove the claim on {removeTarget?.domain ?? 'this domain'}</DialogTitle>
            <DialogDescription>
              SSO discovery and auto-join for this domain stop immediately, and the company's
              organizations stop accepting sign-ins for it. Members who already joined keep their
              access. The reason is recorded in the company's audit trail.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={removeForm.handleSubmit((values) => {
              if (removeTarget) {
                removeMutation.mutate({ claim: removeTarget, reason: values.reason });
              }
            })}
          >
            <FormField
              label="Reason"
              htmlFor="admin-domain-claim-remove-reason"
              required
              error={
                removeForm.formState.errors.reason
                  ? 'Give a reason of up to 500 characters.'
                  : undefined
              }
              hint="Recorded with your user in the enterprise identity audit trail."
            >
              <Textarea
                id="admin-domain-claim-remove-reason"
                rows={3}
                placeholder="Why support is removing this claim, and the ticket"
                {...removeForm.register('reason')}
                data-testid="admin-domain-claim-remove-reason"
              />
            </FormField>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeRemoveDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={removeMutation.isPending}
                data-testid="admin-domain-claim-remove-submit"
              >
                Remove claim
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}
